import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  Button,
  Alert,
  Select,
  Row,
  Input as AntInput,
  Upload,
} from 'antd';
import PropTypes from 'prop-types';
import Input from '../../../../components/elements/input/Input';
import BirthDateInput from '../../../../components/elements/birthDate/BirthDateInput';
import { userPickupMethodOptions } from '../../constants/userConstants';
import {
  buildAddressPayload,
  createEmptyAddressForm,
  normalizeAddressForm,
} from '../../utils/addressForm';
import {
  deleteImage,
  uploadDetachedImage,
} from '../../../../shared/api/imageApi';
import ContactModal from '../../../contacts/components/modals/ContactModal';
import { fetchContactOptions } from '../../../contacts/api/contactsApi';

const manualFieldLabelStyle = {
  display: 'block',
  marginBottom: 6,
  fontWeight: 600,
};

const requiredFieldLabel = (label) => `${label} *`;

const statusOptions = [
  { value: 'active', label: 'Aktivan' },
  { value: 'blocked', label: 'Blokiran' },
  { value: 'deleted', label: 'Obrisan' },
];

const shouldBeContactedOptions = [
  { value: true, label: 'Da' },
  { value: false, label: 'Ne' },
];

const normalizeDateOfBirth = (value) =>
  value ? String(value).slice(0, 10) : '';

const getContactId = (contact) => {
  if (!contact) return null;
  return typeof contact === 'string' ? contact : contact?._id || null;
};

const getContactFullName = (contact) =>
  `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim();

const isContactNotFoundError = (result) => {
  if (result?.status !== 404) return false;
  const details = `${result?.code || ''} ${result?.message || ''}`.toUpperCase();
  return details.includes('CONTACT_NOT_FOUND') || details.includes('KONTAKT');
};

const UpdateUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  onPasswordSubmit,
  onRefreshUser,
  user,
  loading,
}) => {
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [done, setDone] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [originalConsentImage, setOriginalConsentImage] = useState(null);
  const [consentImage, setConsentImage] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState('');
  const [passwordDone, setPasswordDone] = useState(false);
  const [contactOptionsData, setContactOptionsData] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    internalNote: '',
    phone: '',
    lbo: '',
    healthBookletNumber: '',
    isHealthBookletVerified: false,
    pickupMethod: 'APO',
    shouldBeContacted: true,
    status: '',
    address: createEmptyAddressForm(),
    shipmentRecipientFullName: '',
    shipmentRecipientPhone: '',
    shipmentNote: '',
    contact: null,
  });

  const normalizeStatus = (status) => {
    if (status === 'deactivated' || status === 'suspended') return 'blocked';
    return status;
  };

  useEffect(() => {
    if (isOpen && user) {
      const initialConsentImage =
        user?.consentImageIds?.[0] && user?.consentImages?.[0]
          ? {
              id: user.consentImageIds[0],
              url: user.consentImages[0],
              existing: true,
            }
          : null;

      setModalForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        dateOfBirth: normalizeDateOfBirth(user.dateOfBirth),
        email: user.email || '',
        internalNote: user.internalNote || '',
        phone: user.phone || '',
        lbo: user.lbo || '',
        healthBookletNumber: user.healthBookletNumber || '',
        isHealthBookletVerified: Boolean(user.isHealthBookletVerified),
        pickupMethod: user.pickupMethod || 'APO',
        shouldBeContacted: user.shouldBeContacted !== false,
        status: normalizeStatus(user.status) || '',
        address: normalizeAddressForm(user.address),
        shipmentRecipientFullName: user.shipmentRecipientFullName || '',
        shipmentRecipientPhone: user.shipmentRecipientPhone || '',
        shipmentNote: user.shipmentNote || '',
        contact: getContactId(user.contact),
      });
      setOriginalConsentImage(initialConsentImage);
      setConsentImage(initialConsentImage);
      setError('');
      setSuccessMessage('');
      setDone(false);
      setIsPasswordModalOpen(false);
      setPassword('');
      setPasswordError('');
      setPasswordSuccessMessage('');
      setPasswordDone(false);
    }
  }, [isOpen, user]);

  const loadContactOptions = useCallback(async () => {
    setContactsLoading(true);
    const { data, ok, message } = await fetchContactOptions();
    setContactsLoading(false);

    if (!ok) {
      setError(message || 'Doslo je do greske pri dobavljanju kontakata.');
      return;
    }

    setContactOptionsData(data || []);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadContactOptions();
  }, [isOpen, loadContactOptions]);

  // Populisan kontakt iz odgovora backenda - jedini izvor telefona, emaila i informacija.
  const linkedContact = useMemo(
    () =>
      user?.contact && typeof user.contact === 'object' ? user.contact : null,
    [user?.contact]
  );

  const contactOptions = useMemo(
    () =>
      (contactOptionsData || []).map((contact) => ({
        value: contact._id,
        label: contact.name || 'Bez imena',
      })),
    [contactOptionsData]
  );

  const selectedContactLabel = useMemo(() => {
    const option = contactOptions.find(
      (item) => item.value === modalForm.contact
    );
    if (option) return option.label;

    return getContactId(linkedContact) === modalForm.contact
      ? getContactFullName(linkedContact)
      : '';
  }, [contactOptions, linkedContact, modalForm.contact]);

  // Detalji se prikazuju samo za sacuvanu vezu; opcije nose samo _id i name.
  const contactDetails =
    linkedContact && getContactId(linkedContact) === modalForm.contact
      ? linkedContact
      : null;

  const handleContactChange = (value) => {
    setModalForm((prev) => ({ ...prev, contact: value || null }));
  };

  const handleRemoveContact = () => {
    setModalForm((prev) => ({ ...prev, contact: null }));
  };

  const handleContactSaved = useCallback(async () => {
    await loadContactOptions();
    await onRefreshUser?.();
  }, [loadContactOptions, onRefreshUser]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'lbo' || name === 'healthBookletNumber'
        ? value.replace(/\D/g, '')
        : value;
    setModalForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleAddressChange = (field, value) => {
    setModalForm((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleConsentUpload = async (file, onSuccess, onError) => {
    setImageLoading(true);
    setError('');

    try {
      if (consentImage?.id && !consentImage.existing) {
        await deleteImage(consentImage.id);
      }

      const { data, error: uploadError } = await uploadDetachedImage(file);

      if (uploadError) {
        setError(uploadError);
        onError?.(uploadError);
        return;
      }

      const uploadedImage = data?.image || data;
      setConsentImage({
        id: uploadedImage?._id,
        url: uploadedImage?.url,
        existing: false,
      });
      onSuccess?.(uploadedImage);
    } finally {
      setImageLoading(false);
    }
  };

  const handleRemoveConsentImage = async () => {
    if (consentImage?.id && !consentImage.existing) {
      setImageLoading(true);
      const { error: deleteError } = await deleteImage(consentImage.id);
      setImageLoading(false);

      if (deleteError) {
        setError(deleteError);
        return;
      }
    }

    setConsentImage(null);
  };

  // Nov email = nalog ispocetka (kao dodavanje): privremena lozinka se unosi u
  // postojecem modalu za lozinku i salje zajedno sa izmenama.
  const nextEmail = modalForm.email?.trim().toLowerCase() || '';
  const isEmailChanged =
    Boolean(nextEmail) && nextEmail !== (user?.email || '').toLowerCase();

  const emailChangePasswordRef = useRef('');

  const handleUpdate = useCallback(async () => {
    setError('');
    setSuccessMessage('');

    const newPassword = emailChangePasswordRef.current;
    emailChangePasswordRef.current = '';
    if (isEmailChanged && !newPassword) {
      setIsPasswordModalOpen(true);
      setPassword('');
      setPasswordError('');
      setPasswordSuccessMessage('');
      setPasswordDone(false);
      return;
    }

    if (!modalForm.status) {
      setError('Status je obavezan.');
      return;
    }
    if (!modalForm.lbo?.trim()) {
      setError('LBO je obavezan.');
      return;
    }
    if (!modalForm.healthBookletNumber?.trim()) {
      setError('BZK je obavezan.');
      return;
    }

    const result = await onSubmit?.({
      firstName: modalForm.firstName?.trim(),
      lastName: modalForm.lastName?.trim(),
      dateOfBirth: modalForm.dateOfBirth || undefined,
      email: modalForm.email?.trim() || undefined,
      password: isEmailChanged ? newPassword : undefined,
      internalNote: modalForm.internalNote?.trim() || '',
      phone: modalForm.phone?.trim(),
      lbo: modalForm.lbo?.trim() || undefined,
      healthBookletNumber:
        modalForm.healthBookletNumber?.trim() || undefined,
      isHealthBookletVerified: modalForm.isHealthBookletVerified,
      pickupMethod: modalForm.pickupMethod || undefined,
      shouldBeContacted: modalForm.shouldBeContacted,
      role: 'user',
      status: normalizeStatus(modalForm.status),
      consentImages: consentImage?.id ? [consentImage.id] : [],
      address: buildAddressPayload(modalForm.address) || {},
      shipmentRecipientFullName:
        modalForm.shipmentRecipientFullName?.trim() || '',
      shipmentRecipientPhone: modalForm.shipmentRecipientPhone?.trim() || '',
      shipmentNote: modalForm.shipmentNote?.trim() || '',
      contact: modalForm.contact || null,
    });

    if (!result?.ok) {
      if (isContactNotFoundError(result)) {
        setModalForm((prev) => ({ ...prev, contact: null }));
        await loadContactOptions();
        await onRefreshUser?.();
        setError(
          'Izabrana kontakt osoba vise ne postoji. Lista kontakata je osvezena.'
        );
        return;
      }

      setError(result?.message || 'Doslo je do greske pri azuriranju.');
      return;
    }

    if (
      originalConsentImage?.id &&
      originalConsentImage.id !== consentImage?.id
    ) {
      await deleteImage(originalConsentImage.id);
    }

    setSuccessMessage(result?.message || 'Korisnik uspesno azuriran.');
    setDone(true);

    setTimeout(() => {
      setDone(false);
      onClose?.();
    }, 800);
  }, [
    consentImage?.id,
    isEmailChanged,
    loadContactOptions,
    modalForm,
    onClose,
    onRefreshUser,
    onSubmit,
    originalConsentImage?.id,
  ]);

  const resetPasswordModalState = useCallback(() => {
    setIsPasswordModalOpen(false);
    setPassword('');
    setPasswordError('');
    setPasswordSuccessMessage('');
    setPasswordDone(false);
  }, []);

  const handleModalClose = useCallback(async () => {
    if (consentImage?.id && !consentImage.existing) {
      await deleteImage(consentImage.id);
    }

    resetPasswordModalState();
    setIsContactModalOpen(false);
    onClose?.();
  }, [consentImage, onClose, resetPasswordModalState]);

  const handlePasswordSubmit = useCallback(async () => {
    setPasswordError('');
    setPasswordSuccessMessage('');

    if (!password.trim()) {
      setPasswordError('Lozinka je obavezna.');
      return;
    }

    if (isEmailChanged) {
      if (password.trim().length < 6) {
        setPasswordError('Lozinka mora imati najmanje 6 karaktera.');
        return;
      }
      resetPasswordModalState();
      emailChangePasswordRef.current = password.trim();
      await handleUpdate();
      return;
    }

    const result = await onPasswordSubmit?.(password.trim());

    if (!result?.ok) {
      setPasswordError(
        result?.message || 'Doslo je do greske pri azuriranju lozinke.'
      );
      return;
    }

    setPasswordSuccessMessage(
      result?.message || 'Lozinka korisnika uspesno azurirana.'
    );
    setPasswordDone(true);

    setTimeout(() => {
      resetPasswordModalState();
    }, 800);
  }, [
    handleUpdate,
    isEmailChanged,
    onPasswordSubmit,
    password,
    resetPasswordModalState,
  ]);

  const userDisplayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email ||
    'XX';

  return (
    <>
      <Modal
        className="update_user_modal"
        title="Ažuriraj korisnika"
        open={isOpen}
        onCancel={handleModalClose}
        footer={[
          <Button
            key="create"
            type="primary"
            onClick={handleUpdate}
            disabled={loading || done}
            style={{ width: '150px' }}
          >
            {loading ? (
              <img
                src="/assets/icons/add.svg"
                alt="Dodaj"
                className="animate-spin"
              />
            ) : done ? (
              <img
                src="/assets/icons/check.svg"
                alt="Check Icon"
                className="check-icon"
              />
            ) : (
              'Ažuriraj korisnika'
            )}
          </Button>,
        ]}
      >
        <div className="update_user_modal_form" autoComplete="off">
          <Row className="update_user_modal_row">
            <div style={{ order: 1 }}>
              <Input
                label="Ime"
                id="firstName"
                name="firstName"
                placeholder="Ime"
                value={modalForm.firstName}
                onChange={handleOnChange}
              />
            </div>
            <div style={{ order: 2 }}>
              <Input
                label="Prezime"
                id="lastName"
                name="lastName"
                placeholder="Prezime"
                value={modalForm.lastName}
                onChange={handleOnChange}
              />
            </div>
            <div style={{ order: 6 }}>
              <Input
                type="email"
                autoComplete="off"
                label="Email adresa"
                id="email"
                name="email"
                placeholder="Adresa e-poste"
                value={modalForm.email}
                onChange={handleOnChange}
              />
            </div>
            <div style={{ order: 3 }}>
              <Input
                label="Broj telefona"
                id="phone"
                name="phone"
                placeholder="Broj telefona"
                value={modalForm.phone}
                onChange={handleOnChange}
              />
            </div>
            <div style={{ gridColumn: '1 / -1', order: 7 }}>
              <label htmlFor="internalNote" style={manualFieldLabelStyle}>
                Interna napomena
              </label>
              <AntInput.TextArea
                id="internalNote"
                name="internalNote"
                placeholder="Unesite internu napomenu"
                value={modalForm.internalNote}
                onChange={handleOnChange}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            </div>
            <div style={{ order: 4 }}>
              <Input
                required
                label={requiredFieldLabel('LBO')}
                id="lbo"
                name="lbo"
                placeholder={requiredFieldLabel('Unesite LBO broj')}
                value={modalForm.lbo}
                inputMode="numeric"
                onChange={handleOnChange}
              />
            </div>
            <div style={{ order: 5 }}>
              <Input
                required
                label={requiredFieldLabel('BZK')}
                id="healthBookletNumber"
                name="healthBookletNumber"
                placeholder={requiredFieldLabel(
                  'Unesite broj zdravstvene knjizice'
                )}
                value={modalForm.healthBookletNumber}
                inputMode="numeric"
                onChange={handleOnChange}
              />
            </div>
            <div style={{ order: 8 }}>
              <label htmlFor="isHealthBookletVerified" style={manualFieldLabelStyle}>
                Overena knjizica
              </label>
              <Select
                id="isHealthBookletVerified"
                value={modalForm.isHealthBookletVerified}
                onChange={(value) =>
                  setModalForm((prev) => ({
                    ...prev,
                    isHealthBookletVerified: value,
                  }))
                }
                style={{ width: '100%' }}
                options={shouldBeContactedOptions}
              />
            </div>
            <div style={{ order: 9 }}>
              <label htmlFor="pickupMethod" style={manualFieldLabelStyle}>
                {'Način preuzimanja'}
              </label>
              <Select
                id="pickupMethod"
                placeholder="Način preuzimanja"
                value={modalForm.pickupMethod || undefined}
                onChange={(value) =>
                  setModalForm((prev) => ({ ...prev, pickupMethod: value }))
                }
                style={{ width: '100%' }}
                options={userPickupMethodOptions}
              />
            </div>
            <div style={{ order: 10 }}>
              <label htmlFor="dateOfBirth" style={manualFieldLabelStyle}>
                {'Datum rođenja'}
              </label>
              <BirthDateInput
                id="dateOfBirth"
                value={modalForm.dateOfBirth}
                onChange={(v) =>
                  setModalForm((prev) => ({
                    ...prev,
                    dateOfBirth: v,
                  }))
                }
              />
            </div>
            <div style={{ order: 11 }}>
              <label htmlFor="status" style={manualFieldLabelStyle}>
                {requiredFieldLabel('Status korisnika')}
              </label>
              <Select
                id="status"
                placeholder="Status korisnika"
                value={modalForm.status || undefined}
                onChange={(value) =>
                  setModalForm((prev) => ({ ...prev, status: value }))
                }
                style={{ width: '100%' }}
                options={statusOptions}
              />
            </div>
            <div style={{ order: 12 }}>
              <label htmlFor="shouldBeContacted" style={manualFieldLabelStyle}>
                Javljati pacijentu?
              </label>
              <Select
                id="shouldBeContacted"
                value={modalForm.shouldBeContacted}
                onChange={(value) =>
                  setModalForm((prev) => ({
                    ...prev,
                    shouldBeContacted: value,
                  }))
                }
                style={{ width: '100%' }}
                options={shouldBeContactedOptions}
              />
            </div>
            <div style={{ order: 13 }}>
              <Button
                style={{ width: '100%' }}
                onClick={() => {
                  setIsPasswordModalOpen(true);
                  setPassword('');
                  setPasswordError('');
                  setPasswordSuccessMessage('');
                  setPasswordDone(false);
                }}
                disabled={loading}
              >
                Izmeni lozinku korisniku
              </Button>
            </div>
            <div style={{ gridColumn: '1 / -1', order: 14 }}>
              <label style={manualFieldLabelStyle}>Slika saglasnosti</label>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 12,
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <img
                  src={
                    consentImage?.url || '/assets/icons/consent-placeholder.svg'
                  }
                  alt="Saglasnost"
                  style={{
                    width: 112,
                    height: 112,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0',
                    padding: consentImage?.url ? 0 : 12,
                    background: '#fafafa',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Upload
                    accept="image/*"
                    maxCount={1}
                    name="image"
                    showUploadList={false}
                    customRequest={async ({ file, onSuccess, onError }) =>
                      handleConsentUpload(file, onSuccess, onError)
                    }
                  >
                    <Button
                      size="small"
                      loading={imageLoading}
                      disabled={loading}
                    >
                      {consentImage ? 'Zameni saglasnost' : 'Dodaj saglasnost'}
                    </Button>
                  </Upload>
                  {consentImage && (
                    <Button
                      size="small"
                      danger
                      onClick={handleRemoveConsentImage}
                      disabled={imageLoading || loading}
                    >
                      Obrisi
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1', order: 15 }}>
              <label style={{ display: 'block', marginBottom: 6 }}>Adresa</label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 16,
                }}
              >
                <div>
                  <Input
                    label="Ulica"
                    id="addressStreet"
                    name="addressStreet"
                    placeholder="Ulica i broj"
                    value={modalForm.address.street}
                    onChange={(e) =>
                      handleAddressChange('street', e.target.value)
                    }
                  />
                </div>
                <div>
                  <Input
                    label="Grad"
                    id="addressCity"
                    name="addressCity"
                    placeholder="Grad"
                    value={modalForm.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label="Postanski broj"
                    id="addressPostalCode"
                    name="addressPostalCode"
                    placeholder="Postanski broj"
                    value={modalForm.address.postalCode}
                    onChange={(e) =>
                      handleAddressChange('postalCode', e.target.value)
                    }
                  />
                </div>
                <div>
                  <Input
                    label="Drzava"
                    id="addressCountry"
                    name="addressCountry"
                    placeholder="Drzava"
                    value={modalForm.address.country}
                    onChange={(e) =>
                      handleAddressChange('country', e.target.value)
                    }
                  />
                </div>
                <div>
                  <Input
                    label="Ime i prezime primaoca posiljke"
                    id="shipmentRecipientFullName"
                    name="shipmentRecipientFullName"
                    placeholder="Ime i prezime primaoca posiljke"
                    value={modalForm.shipmentRecipientFullName}
                    onChange={handleOnChange}
                  />
                </div>
                <div>
                  <Input
                    label="Telefon primaoca posiljke"
                    id="shipmentRecipientPhone"
                    name="shipmentRecipientPhone"
                    placeholder="Telefon primaoca posiljke"
                    value={modalForm.shipmentRecipientPhone}
                    onChange={handleOnChange}
                  />
                </div>
                <div>
                  <Input
                    label="Napomena o posiljci"
                    id="shipmentNote"
                    name="shipmentNote"
                    placeholder="Napomena o posiljci"
                    value={modalForm.shipmentNote}
                    onChange={handleOnChange}
                  />
                </div>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1', order: 16 }}>
              <span
                style={{
                  display: 'block',
                  marginBottom: 6,
                  fontWeight: 'bold',
                }}
              >
                Kontakt osoba
              </span>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Select
                    id="contact"
                    showSearch
                    allowClear
                    placeholder="Uvezi sa kontakt osobom"
                    value={modalForm.contact || undefined}
                    onChange={handleContactChange}
                    options={contactOptions}
                    loading={contactsLoading}
                    optionFilterProp="label"
                    style={{ flex: '1 1 260px', minWidth: 220 }}
                    notFoundContent={
                      contactsLoading ? 'Ucitavanje...' : 'Nema kontakata'
                    }
                  />
                  <Button
                    onClick={() => setIsContactModalOpen(true)}
                    disabled={!contactDetails || loading}
                  >
                    Izmeni kontakt
                  </Button>
                  <Button
                    danger
                    onClick={handleRemoveContact}
                    disabled={!modalForm.contact || loading}
                  >
                    Ukloni
                  </Button>
                </div>
                {contactDetails ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 12,
                    }}
                  >
                    <div>
                      <span style={manualFieldLabelStyle}>Ime i prezime</span>
                      {getContactFullName(contactDetails) || '-'}
                    </div>
                    <div>
                      <span style={manualFieldLabelStyle}>Broj telefona</span>
                      {contactDetails.phone || '-'}
                    </div>
                    <div>
                      <span style={manualFieldLabelStyle}>Email</span>
                      {contactDetails.email || '-'}
                    </div>
                    <div>
                      <span style={manualFieldLabelStyle}>Informacije</span>
                      {contactDetails.info || '-'}
                    </div>
                  </div>
                ) : (
                  <span style={{ color: '#707374' }}>
                    {modalForm.contact
                      ? `Izabrano: ${selectedContactLabel || 'kontakt osoba'}. Detalji ce biti prikazani nakon cuvanja izmena.`
                      : 'Nije povezana kontakt osoba.'}
                  </span>
                )}
              </div>
            </div>
          </Row>

          {successMessage && (
            <Alert
              title={successMessage}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {error && (
            <Alert
              title={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </div>
      </Modal>

      <Modal
        title="Dodaj lozinku"
        open={isPasswordModalOpen}
        onCancel={resetPasswordModalState}
        footer={[
          <Button
            key="submit-password"
            type="primary"
            onClick={handlePasswordSubmit}
            disabled={loading || passwordDone}
          >
            Dodaj
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 16 }}>
          Dodaj novu lozinku korisniku {userDisplayName}
        </p>
        {isEmailChanged && (
          <p style={{ marginBottom: 16 }}>
            Email je promenjen, pa je ovo privremena lozinka. Korisnik dobija
            mejl, ponovo prihvata uslove i politiku i postavlja svoju lozinku.
            Klikom na Dodaj cuvaju se i ostale izmene.
          </p>
        )}
        <AntInput.Password
          placeholder="Unesite novu lozinku"
          autoComplete="new-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (passwordError) setPasswordError('');
            if (passwordSuccessMessage) setPasswordSuccessMessage('');
          }}
          onPressEnter={handlePasswordSubmit}
        />
        {passwordSuccessMessage && (
          <Alert
            title={passwordSuccessMessage}
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {passwordError && (
          <Alert
            title={passwordError}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Modal>
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSaved={handleContactSaved}
        contact={contactDetails}
      />
    </>
  );
};

UpdateUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPasswordSubmit: PropTypes.func,
  onRefreshUser: PropTypes.func,
  onSubmit: PropTypes.func,
  user: PropTypes.object,
  loading: PropTypes.bool,
};

export default UpdateUserModal;
